import get from 'lodash/get'
import { Notification } from 'element-ui'

// User-facing text for API errors (mirror of v2 `shared/utils/api-error.ts`).
// - v1 API: `{ error: { code, msg } }` — `msg` is Vietnamese when the code is in
//   `api/src/lang/vi.json`, otherwise it equals the code.
// - api-v2: `{ message, userMessage? }` — `message` is the code, `userMessage`
//   the Vietnamese text.

export const DEFAULT_ERROR_MESSAGE = 'Có lỗi xảy ra, vui lòng thử lại.'
const NETWORK_ERROR_MESSAGE = 'Không kết nối được máy chủ, vui lòng kiểm tra mạng.'
const TIMEOUT_ERROR_MESSAGE = 'Máy chủ phản hồi quá lâu, vui lòng thử lại.'

const HTTP_MESSAGES = {
  UNAUTHORIZED: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.',
  'FORBIDDEN RESOURCE': 'Bạn không có quyền thực hiện thao tác này.',
  FORBIDDEN: 'Bạn không có quyền thực hiện thao tác này.',
  'NOT FOUND': 'Không tìm thấy dữ liệu.',
  'INTERNAL SERVER ERROR': 'Lỗi hệ thống, vui lòng thử lại sau.'
}

const STATUS_MESSAGES = {
  401: HTTP_MESSAGES.UNAUTHORIZED,
  403: HTTP_MESSAGES.FORBIDDEN,
  404: HTTP_MESSAGES['NOT FOUND'],
  413: 'Dữ liệu gửi lên quá lớn.',
  429: 'Bạn thao tác quá nhanh, vui lòng thử lại sau ít phút.',
  500: HTTP_MESSAGES['INTERNAL SERVER ERROR'],
  502: 'Hệ thống đang bảo trì hoặc quá tải, vui lòng thử lại sau.',
  503: 'Hệ thống đang bảo trì hoặc quá tải, vui lòng thử lại sau.',
  504: TIMEOUT_ERROR_MESSAGE
}

const CODE_RE = /^E_[A-Z0-9_]+$/

const str = (v) => {
  if (Array.isArray(v)) v = v[0]
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}

function textOf (raw) {
  const known = HTTP_MESSAGES[raw.toUpperCase()]
  if (known) return known
  if (/^E_MONGOOSE_/.test(raw)) return 'Dữ liệu không hợp lệ.'
  if (CODE_RE.test(raw)) {
    // Unknown code: never show it to users; keep it for whoever debugs.
    console.warn('[api-error] no message for', raw)
    return DEFAULT_ERROR_MESSAGE
  }
  // class-validator messages ("fullname should not be empty", …).
  return `Dữ liệu không hợp lệ: ${raw}`
}

/** Vietnamese message for an axios error. */
export function apiErrorMessage (error) {
  const data = get(error, 'response.data') || {}
  const userMessage = str(data.userMessage)
  if (userMessage) return userMessage
  const v1Msg = str(get(data, 'error.msg'))
  if (v1Msg && !CODE_RE.test(v1Msg)) return v1Msg

  const raw = str(get(data, 'error.code')) || v1Msg || str(data.message)
  if (raw) return textOf(raw)

  const status = get(error, 'response.status')
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status]
  const code = error && error.code
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return TIMEOUT_ERROR_MESSAGE
  if (code === 'ERR_NETWORK' || (error && error.message && !error.response)) {
    return NETWORK_ERROR_MESSAGE
  }
  return DEFAULT_ERROR_MESSAGE
}

// One toast per distinct message while it is on screen: a page whose N
// requests fail for the same reason (expired session, network down, no
// permission) shows one notification instead of N.
const DEDUPE_MS = 4500
const shownAt = new Map()

export function notifyError (message) {
  const now = Date.now()
  const last = shownAt.get(message)
  if (last && now - last < DEDUPE_MS) return
  shownAt.set(message, now)
  Notification({ type: 'error', title: 'Lỗi', message, duration: DEDUPE_MS })
}

/** Interceptor handler shared by the v1 and api-v2 axios instances. */
export function handleApiError (error) {
  if (process.env.NODE_ENV !== 'production') {
    console.error(error)
  }
  // A request the page cancelled itself is not an error for the user.
  const canceled = error && (error.__CANCEL__ || error.code === 'ERR_CANCELED')
  if (!canceled) notifyError(apiErrorMessage(error))
  return Promise.reject(error)
}
